// src/components/APanel/AdminHome.jsx
//
// Pagina de start a adminului și a managerului (APanel, MPanel). Sus, în afara
// casetelor, pe un rând (ca la Lecții practice, dar fără panou în jur): meniul
// „ce se vede” (DropdownUI: Studenți și grupe / Studenți / Grupe / Lecții suplimentare),
// căutarea, semaforul cu stările programării (StatusFilterUI) și „Grupă nouă” (doar admin,
// ButtonUI lg). Dedesubt, cele două casete; primesc căutarea și filtrul de aici, nu mai au
// fiecare câte unul. „Lecții suplimentare”: în locul semaforului, luna (DropdownUI) și cine
// (ai noștri / din alte școli); dedesubt elevii lunii (ExtraLessonsCard).

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { UserContext } from "../../UserContext";
import { normalizeRole, ROLES } from "../../auth/access";
import { useBookingStatusMonitor } from "../../hooks/useBookingStatusMonitor";
import { openPopup } from "../Common/popupUIStore";
import { ButtonUI, DropdownUI, SearchBarUI, StatusFilterUI } from "../UI";
import addIcon from "../../assets/svg/add.svg";
import { fetchExtraLessons } from "../../api/extraLessonsService";
import ExtraLessonsCard from "./ExtraLessons/ExtraLessonsCard";
import { KINDS, groupByMonth } from "./ExtraLessons/extraLessonsModel";

import StudentsManager from "./StudentsManager";
import GroupManager from "./GroupManager";

// pe telefon „Grupă nouă” rămâne doar cu iconița, ca să încapă căutarea
const NARROW = "(max-width: 768px)";

function useNarrow() {
   const [narrow, setNarrow] = useState(
      () => typeof window !== "undefined" && window.matchMedia(NARROW).matches,
   );
   useEffect(() => {
      const mq = window.matchMedia(NARROW);
      const onChange = () => setNarrow(mq.matches);
      onChange();
      mq.addEventListener?.("change", onChange);
      return () => mq.removeEventListener?.("change", onChange);
   }, []);
   return narrow;
}

const withCount = (text, n) => (n == null ? text : `${text} · ${n}`);

export default function AdminHome() {
   const { user } = useContext(UserContext);
   const role = normalizeRole(user?.role);
   const isAdmin = role === ROLES.ADMIN;
   // starea programării: o singură cerere pentru ambele casete
   const bookingMonitor = useBookingStatusMonitor(
      role === ROLES.ADMIN || role === ROLES.MANAGER,
   );
   const narrow = useNarrow();

   const [view, setView] = useState("all");
   const [query, setQuery] = useState("");
   const [statusFilter, setStatusFilter] = useState(null);

   // câte rezultate are fiecare casetă (după căutare și filtru), în meniu
   const [counts, setCounts] = useState({ students: null, groups: null });
   const onStudentsCount = useCallback(
      (n) => setCounts((c) => (c.students === n ? c : { ...c, students: n })),
      [],
   );
   const onGroupsCount = useCallback(
      (n) => setCounts((c) => (c.groups === n ? c : { ...c, groups: n })),
      [],
   );

   // „Lecții suplimentare”: se încarcă la prima deschidere a vederii (GET /extra-lessons)
   const [extra, setExtra] = useState({ students: null, loading: false, error: "" });
   const [extraMonth, setExtraMonth] = useState("");
   const [extraKind, setExtraKind] = useState("all");
   const loadExtra = useCallback(async () => {
      setExtra((e) => ({ ...e, loading: true, error: "" }));
      try {
         const res = await fetchExtraLessons();
         setExtra({ students: Array.isArray(res?.students) ? res.students : [], loading: false, error: "" });
      } catch (e) {
         setExtra((prev) => ({ students: prev.students || [], loading: false, error: e?.message || "Nu am putut încărca lista." }));
      }
   }, []);
   useEffect(() => {
      if (view === "extra" && extra.students === null && !extra.loading) loadExtra();
   }, [view, extra.students, extra.loading, loadExtra]);

   const months = useMemo(
      () => groupByMonth(extra.students || [], { kind: extraKind, query }),
      [extra.students, extraKind, query],
   );
   const extraTotal = extra.students ? months.reduce((n, m) => n + m.count, 0) : null;
   // luna: cea aleasă, dacă mai are elevi după filtre; altfel cea mai nouă
   const month = extraMonth === "all" || months.some((m) => m.month === extraMonth) ? extraMonth : months[0]?.month || "all";
   const monthOptions = [
      { value: "all", label: withCount("Toate lunile", extraTotal) },
      ...months.map((m) => ({ value: m.month, label: withCount(m.label, m.count) })),
   ];

   const viewOptions = [
      { value: "all", label: "Studenți și grupe" },
      { value: "students", label: withCount("Studenți", counts.students) },
      { value: "groups", label: withCount("Grupe", counts.groups) },
      { value: "extra", label: withCount("Lecții suplimentare", extraTotal) },
   ];
   const isExtra = view === "extra";

   return (
      <section className={`home-admin home-admin--${view}`}>
         <div className="home-admin__toolbar pageToolbar">
            <DropdownUI
               size="lg"
               surface="page"
               value={view}
               onChange={setView}
               options={viewOptions}
               aria-label="Ce se vede"
               className="pageToolbar__menu"
            />

            <SearchBarUI
               surface="page"
               value={query}
               onChange={(e) => setQuery(e.target.value)}
               onClear={() => setQuery("")}
               placeholder="Caută după nume / telefon / email…"
               aria-label="Caută student"
               className="pageToolbar__search"
            />

            <div className="pageToolbar__controls">
               {isExtra ? (
                  <>
                     <DropdownUI
                        size="lg"
                        surface="page"
                        value={month}
                        onChange={setExtraMonth}
                        options={monthOptions}
                        disabled={!extra.students}
                        aria-label="Luna"
                        className="home-admin__month"
                     />
                     <DropdownUI
                        size="lg"
                        surface="page"
                        align="end"
                        value={extraKind}
                        onChange={setExtraKind}
                        options={KINDS}
                        aria-label="Ce elevi"
                     />
                  </>
               ) : null}
               {!isExtra && bookingMonitor.enabled && (
                  <StatusFilterUI
                     surface="page"
                     value={statusFilter}
                     onChange={setStatusFilter}
                  />
               )}
               {!isExtra && isAdmin && (
                  <ButtonUI
                     variant="primary"
                     size="lg"
                     icon={addIcon}
                     aria-label="Grupă nouă"
                     title="Grupă nouă"
                     onClick={() => openPopup("groupEdit", { mode: "create" })}
                  >
                     {narrow ? null : "Grupă nouă"}
                  </ButtonUI>
               )}
            </div>
         </div>

         {/* ambele rămân montate: meniul doar le ascunde (numerele rămân la zi) */}
         <StudentsManager
            query={query}
            statusFilter={statusFilter}
            bookingMonitor={bookingMonitor}
            onCount={onStudentsCount}
         />
         <GroupManager
            query={query}
            statusFilter={statusFilter}
            bookingMonitor={bookingMonitor}
            onCount={onGroupsCount}
         />
         {isExtra ? (
            <ExtraLessonsCard
               months={months}
               month={month}
               kind={extraKind}
               query={query}
               loading={extra.loading && !extra.students}
               error={extra.error}
               onReload={loadExtra}
            />
         ) : null}
      </section>
   );
}
